import { useState, useEffect } from 'react';
import { apiCall } from '../config/api';

/**
 * Fetches `/api/philosophy` (default current) or `/api/philosophy/versions/{n}`
 * and returns { philosophy, loading, error }.
 *
 * Pass null/undefined for `version` to load the current alias; pass a number
 * to load that specific historical version.
 */
export const usePhilosophy = (version) => {
  const [philosophy, setPhilosophy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const path = version ? `/philosophy/versions/${version}` : '/philosophy';
    apiCall(path)
      .then((data) => { if (!cancelled) setPhilosophy(data); })
      .catch((err) => {
        if (!cancelled) {
          // 404 from the current endpoint just means no philosophy has
          // been generated yet -- not a real error to show.
          setPhilosophy(null);
          setError(err?.message || 'Failed to load philosophy');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [version]);

  return { philosophy, loading, error };
};

/**
 * Fetches `/api/philosophy/versions` -- the metadata-only list used by the
 * picker. Returns { versions, loading, error }.
 */
export const usePhilosophyVersions = () => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiCall('/philosophy/versions')
      .then((data) => { if (!cancelled) setVersions(data?.versions || []); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load versions'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  return { versions, loading, error };
};
